import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  Button,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment,
  School,
  EmojiEvents,
  TrendingUp,
  CheckCircle,
  Cancel,
  Groups,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const ResultsSummary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [error, setError] = useState(null);

  const BRAND = {
    a: '#5c55f3',
    b: '#7c75ff',
    gradient: 'linear-gradient(135deg, #5c55f3 0%, #7c75ff 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  const grades = [6, 7, 8, 9, 10, 11];

  const gradeColors = {
    'A': '#16a34a',   // Green
    'B': '#3b82f6',   // Blue
    'C': '#f59e0b',   // Orange
    'S': '#f97316',   // Dark Orange
    'F': '#ef4444',   // Red
  };

  useEffect(() => {
    fetchTeacherProfile();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      fetchSummary();
    }
  }, [selectedSubject, selectedGrade, selectedExam]);

  // Reset exam filter when grade changes
  useEffect(() => {
    if (selectedGrade && selectedExam) {
      setSelectedExam('');
    }
  }, [selectedGrade]);

  const fetchTeacherProfile = async () => {
    setProfileLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/teacher/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns: { teacher: { subjects: [...], name: ..., etc } }
      const subjects = response.data.teacher?.subjects || response.data.subjects || [];
      
      setTeacherSubjects(subjects);
      
      if (subjects.length > 0) {
        setSelectedSubject(subjects[0]);
      } else {
        setError('No subjects assigned. Please go to dashboard and use "Edit Subjects" to assign Mathematics or Science.');
      }
    } catch (error) {
      setError('Failed to load teacher profile. Please ensure you are logged in as a teacher.');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        subject: selectedSubject,
      });
      
      if (selectedGrade) params.append('grade', selectedGrade);
      if (selectedExam) params.append('exam_id', selectedExam);

      const response = await axios.get(`${API_URL}/teacher/results-summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSummaryData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedGrade('');
    setSelectedExam('');
  };

  if (profileLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: BRAND.a, mb: 3 }} />
          <Typography variant="h6" sx={{ color: BRAND.muted }}>
            Loading your subjects...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error || teacherSubjects.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <School sx={{ fontSize: 80, color: '#cbd5e1', mb: 3 }} />
          <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink, mb: 2 }}>
            {error || 'No Subjects Assigned'}
          </Typography>
          <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
            {error ? 'There was an error loading your profile.' : 'You need to have subjects assigned to view results summary.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              sx={{
                background: BRAND.gradient,
                color: 'white',
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  opacity: 0.9,
                }
              }}
            >
              Go to Dashboard
            </Button>
            {error && (
              <Button
                variant="outlined"
                onClick={fetchTeacherProfile}
                sx={{
                  borderColor: BRAND.a,
                  color: BRAND.a,
                  fontWeight: 600,
                  px: 4,
                  '&:hover': {
                    borderColor: BRAND.b,
                    bgcolor: alpha(BRAND.a, 0.05),
                  }
                }}
              >
                Retry
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: BRAND.gradient,
      py: { xs: 3, md: 4 },
      px: { xs: 2, md: 4 },
    }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4, 
        background: BRAND.surface,
        backdropFilter: 'blur(10px)',
        p: { xs: 3, md: 4 }, 
        borderRadius: 4,
        boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.18)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          background: BRAND.gradient,
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/dashboard')}
              sx={{
                color: BRAND.a,
                fontWeight: 600,
                '&:hover': {
                  bgcolor: alpha(BRAND.a, 0.08),
                }
              }}
            >
              Back
            </Button>
            <Box>
              <Typography variant="h3" fontWeight="800" sx={{ 
                background: BRAND.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}>
                📊 Results Summary
              </Typography>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Comprehensive analysis of student performance
              </Typography>
            </Box>
          </Box>
          <Avatar sx={{ 
            width: 70, 
            height: 70, 
            background: BRAND.gradient,
            fontSize: 32,
            fontWeight: 'bold',
            boxShadow: '0 8px 24px rgba(92, 85, 243, 0.4)'
          }}>
            {user?.name?.charAt(0)}
          </Avatar>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ 
        p: 3,
        mb: 4,
        background: BRAND.surface,
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
        border: '1px solid rgba(255,255,255,0.18)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box sx={{
            width: 4,
            height: 32,
            background: BRAND.gradient,
            borderRadius: 2,
            mr: 2
          }} />
          <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
            Filters
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Subject"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              size="small"
              helperText={`${teacherSubjects.length} subject(s) assigned`}
            >
              {teacherSubjects.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Grade"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              size="small"
              helperText={selectedGrade ? `Showing Grade ${selectedGrade}` : 'All grades included'}
            >
              <MenuItem value="">All Grades</MenuItem>
              {grades.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  Grade {grade}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Exam"
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
              size="small"
              disabled={!summaryData?.available_exams?.length}
              helperText={summaryData?.available_exams?.length ? `${summaryData.available_exams.length} exam(s) available` : 'No exams found'}
            >
              <MenuItem value="">All Exams</MenuItem>
              {summaryData?.available_exams?.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.name} (Grade {exam.grade}) - {exam.exam_date}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                height: '40px',
                borderColor: BRAND.a,
                color: BRAND.a,
                '&:hover': {
                  borderColor: BRAND.b,
                  bgcolor: alpha(BRAND.a, 0.05),
                }
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {summaryData?.filters_applied && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: BRAND.muted, mr: 1, alignSelf: 'center', fontWeight: 600 }}>
              Active Filters:
            </Typography>
            <Chip label={summaryData.filters_applied.subject} size="small" sx={{ bgcolor: alpha(BRAND.a, 0.1), color: BRAND.a, fontWeight: 600 }} />
            <Chip label={summaryData.filters_applied.grade} size="small" sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 600 }} />
            <Chip label={summaryData.filters_applied.exam} size="small" sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', fontWeight: 600 }} />
          </Box>
        )}
        
        {selectedGrade && summaryData?.available_exams?.length > 0 && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha('#3b82f6', 0.08), borderRadius: 2, border: `1px solid ${alpha('#3b82f6', 0.2)}` }}>
            <Typography variant="caption" sx={{ color: '#1e40af', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              💡 <strong>{summaryData.available_exams.length} exam(s)</strong> found for Grade {selectedGrade} in {selectedSubject}
            </Typography>
          </Box>
        )}
      </Paper>

      {loading && (
        <Paper sx={{ p: 6, textAlign: 'center', background: BRAND.surface, borderRadius: 3 }}>
          <CircularProgress size={60} sx={{ color: BRAND.a, mb: 3 }} />
          <Typography variant="h6" sx={{ color: BRAND.muted }}>
            Loading results summary...
          </Typography>
        </Paper>
      )}

      {!loading && summaryData && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Groups sx={{ fontSize: 40 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Total Students
                    </Typography>
                  </Box>
                  <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 2 }}>
                    {summaryData.statistics.total_students}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(17, 153, 142, 0.4)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <CheckCircle sx={{ fontSize: 40 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Present Students
                    </Typography>
                  </Box>
                  <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 2 }}>
                    {summaryData.statistics.present_students}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                color: 'white',
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 12px 40px rgba(250, 112, 154, 0.4)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Cancel sx={{ fontSize: 40 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Absent Students
                    </Typography>
                  </Box>
                  <Typography variant="h2" fontWeight="bold" align="center" sx={{ my: 2 }}>
                    {summaryData.statistics.absent_students}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Grade Distribution Table */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ 
                p: 4,
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                border: '1px solid rgba(255,255,255,0.18)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 4,
                    height: 32,
                    background: BRAND.gradient,
                    borderRadius: 2,
                    mr: 2
                  }} />
                  <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                    Grade Distribution
                  </Typography>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(BRAND.a, 0.1) }}>
                        <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Grade</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Count</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Average Marks</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Average %</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summaryData.grade_distribution.filter(gradeData => gradeColors[gradeData.grade]).map((gradeData) => (
                        <TableRow 
                          key={gradeData.grade}
                          sx={{ 
                            '&:hover': { bgcolor: alpha(gradeColors[gradeData.grade] || '#94a3b8', 0.05) },
                            transition: 'background-color 0.2s'
                          }}
                        >
                          <TableCell>
                            <Chip 
                              label={gradeData.grade}
                              sx={{
                                bgcolor: alpha(gradeColors[gradeData.grade], 0.15),
                                color: gradeColors[gradeData.grade],
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                minWidth: 50
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="h6" fontWeight="700" sx={{ color: BRAND.ink }}>
                              {gradeData.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.muted }}>
                              {gradeData.average_marks}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.muted }}>
                              {gradeData.average_percentage}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* Grade Distribution Pie Chart */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ 
                p: 4,
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
                border: '1px solid rgba(255,255,255,0.18)',
                height: '100%',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 4,
                    height: 32,
                    background: BRAND.gradient,
                    borderRadius: 2,
                    mr: 2
                  }} />
                  <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                    Visual Distribution
                  </Typography>
                </Box>

                  {summaryData.grade_distribution.some(g => g.count > 0 && gradeColors[g.grade]) ? (
                  <>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={summaryData.grade_distribution.filter(g => g.count > 0 && gradeColors[g.grade])}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.grade}: ${entry.count}`}
                          outerRadius={90}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {summaryData.grade_distribution.filter(g => g.count > 0 && gradeColors[g.grade]).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={gradeColors[entry.grade] || '#94a3b8'} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            background: 'rgba(255, 255, 255, 0.98)',
                            border: '2px solid #5c55f3',
                            borderRadius: 8,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            padding: '12px 16px'
                          }}
                          formatter={(value, name, props) => [
                            `${value} students (${props.payload.average_percentage}% avg)`,
                            `Grade ${props.payload.grade}`
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>

                    <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                      {summaryData.grade_distribution.filter(g => g.count > 0 && gradeColors[g.grade]).map((grade) => (
                        <Box key={grade.grade} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: gradeColors[grade.grade] || '#94a3b8'
                          }} />
                          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                            {grade.grade} ({grade.count})
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Assessment sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                    <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                      No grade data available
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Top 10 Students */}
          <Paper sx={{ 
            p: 4,
            background: BRAND.surface,
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
            border: '1px solid rgba(255,255,255,0.18)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{
                width: 4,
                height: 32,
                background: BRAND.gradient,
                borderRadius: 2,
                mr: 2
              }} />
              <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                🏆 Top 10 Students (Highest Marks)
              </Typography>
            </Box>

            {summaryData.top_students && summaryData.top_students.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(BRAND.a, 0.1) }}>
                      <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Student ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: BRAND.ink }}>Exam</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Marks</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Percentage</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: BRAND.ink }}>Grade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryData.top_students.map((student, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:hover': { bgcolor: alpha('#fbbf24', 0.05) },
                          transition: 'background-color 0.2s',
                          bgcolor: index < 3 ? alpha('#fbbf24', 0.08) : 'transparent'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {index < 3 && <EmojiEvents sx={{ color: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : '#cd7f32', fontSize: 24 }} />}
                            <Typography variant="h6" fontWeight="700" sx={{ color: BRAND.ink }}>
                              #{index + 1}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                            {student.student_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: BRAND.muted, fontFamily: 'monospace' }}>
                            {student.student_index}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: BRAND.muted }}>
                            {student.exam_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" fontWeight="600" sx={{ color: BRAND.ink }}>
                            {student.marks_obtained}/{student.total_marks}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" fontWeight="700" sx={{ color: '#10b981' }}>
                            {student.percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={student.grade || 'N/A'}
                            sx={{
                              bgcolor: alpha(gradeColors[student.grade] || '#94a3b8', 0.15),
                              color: gradeColors[student.grade] || '#94a3b8',
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              minWidth: 50
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <School sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  No student results available
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                  Results will appear here once students take exams
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ResultsSummary;
